import {
  AutocompleteInput,
  CheckboxInput,
  ConditionalInput,
  ConfigurationContext,
  DataTableExRef,
  DateInput,
  FileInput,
  FormDialog,
  FormGetter,
  FormInput,
  FormPageLayout,
  FormSetter,
  HiddenInput,
  SelectInput,
  SubmitButton,
  useNavigate,
} from '@kirz/mui-admin';
import { Add, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  InputAdornment,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Calendar,
  ClipboardListOutline,
  ContentCopy,
  ViewGridOutline,
  Waves as WavesIcon,
} from 'mdi-material-ui';
import React, { useContext, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { FormNavigateButton } from 'components/FormNavigateButton';
import { SpotVariantsTable } from 'components/SpotVariantsTable';
import { SpotType, SpotTypes } from 'constants/other';
import { AppStateContext } from 'contexts/AppStateContext';
import { UserContext } from 'contexts/UserContext';
import { useAppState } from 'hooks/useAppState';

function UploadFile({ tableRef }: any) {
  const { user } = useContext(UserContext);
  const routeParams = useParams() as Record<string, string>;
  const spotId = parseInt(routeParams.id, 10);
  const { hasura } = useContext(ConfigurationContext);
  const [isUploading, setIsUploading] = useState(false);

  return (
    <>
      {!isUploading && (
        <FileInput
          name="fileId"
          mode="hasura"
          formFetcherValueResolver={null}
          formSubmitterValueResolver={null}
          md={12}
        />
      )}

      <FormSetter
        render={(setValue) => {
          return (
            <FormGetter
              names={['fileId']}
              onChange={async ({ fileId }) => {
                if (!fileId) {
                  return;
                }

                setIsUploading(true);

                await hasura.request({
                  type: 'mutation',
                  action: 'insertOne',
                  source: 'spotVariant',
                  item: {
                    companyId: user.companyId,
                    spotId,
                    fileId,
                  },
                });

                setIsUploading(false);

                tableRef.current?.reload();
              }}
            />
          );
        }}
      />
    </>
  );
}

function CopySpotDialog({ open, onClose, currentSpot, user }: any) {
  const { hasura } = useContext(ConfigurationContext);
  const navigate = useNavigate();

  const handleSubmit = async (values: any) => {
    const { stationId: targetStationId, daypartId: targetDaypartId } = values;
    const { companyId } = user;

    // 1. Fetch current spot's full details
    const [fullSpot]: any = await hasura.request({
      type: 'query',
      source: 'waveSpot',
      where: { id: { _eq: currentSpot.id } },
      selection:
        'id name startDate endDate type weight numberPerDay connectionId appendToEmptyBlocks connectionOffset isPlanConfirmed campaign { id name } wave { id name }',
      limit: 1,
    });

    if (!fullSpot) {
      console.error('Spot not found');
      return;
    }

    // 2. Fetch or Create Campaign in target station
    let targetCampaignId;
    const [existingCampaign]: any = await hasura.request({
      type: 'query',
      source: 'campaign',
      where: {
        name: { _eq: fullSpot.campaign.name },
        stationId: { _eq: targetStationId },
        companyId: { _eq: companyId },
      },
      selection: 'id',
      limit: 1,
    });

    if (existingCampaign) {
      targetCampaignId = existingCampaign.id;
    } else {
      const newCampaign = await hasura.request({
        type: 'mutation',
        action: 'insertOne',
        source: 'campaign',
        item: {
          name: fullSpot.campaign.name,
          stationId: targetStationId,
          companyId,
        },
        selection: 'id',
      });
      targetCampaignId = newCampaign.id;
    }

    // 3. Fetch or Create Wave in target campaign
    let targetWaveId;
    const [existingWave]: any = await hasura.request({
      type: 'query',
      source: 'wave',
      where: {
        name: { _eq: fullSpot.wave.name },
        campaignId: { _eq: targetCampaignId },
        stationId: { _eq: targetStationId },
        companyId: { _eq: companyId },
      },
      selection: 'id',
      limit: 1,
    });

    if (existingWave) {
      targetWaveId = existingWave.id;
    } else {
      const newWave = await hasura.request({
        type: 'mutation',
        action: 'insertOne',
        source: 'wave',
        item: {
          name: fullSpot.wave.name,
          campaignId: targetCampaignId,
          stationId: targetStationId,
          companyId,
        },
        selection: 'id',
      });
      targetWaveId = newWave.id;
    }

    // 5. Duplicate Spot
    const newSpot = await hasura.request({
      type: 'mutation',
      action: 'insertOne',
      source: 'waveSpot',
      item: {
        companyId,
        stationId: targetStationId,
        campaignId: targetCampaignId,
        waveId: targetWaveId,
        daypartId: targetDaypartId,
        name: fullSpot.name,
        startDate: fullSpot.startDate,
        endDate: fullSpot.endDate,
        type: fullSpot.type,
        weight: fullSpot.weight,
        numberPerDay: fullSpot.numberPerDay,
        connectionId: fullSpot.connectionId,
        connectionOffset: fullSpot.connectionOffset,
        appendToEmptyBlocks: fullSpot.appendToEmptyBlocks,
        isPlanConfirmed: false,
      },
      selection: 'id',
    });

    const newSpotId = newSpot.id;

    // 6. Duplicate Variants
    const variants = await hasura.request({
      type: 'query',
      source: 'spotVariant',
      where: { spotId: { _eq: fullSpot.id }, companyId: { _eq: companyId } },
      selection: 'fileId weight mixPoint',
    });

    await hasura.request({
      type: 'mutation',
      action: 'insert',
      source: 'spotVariant',
      items: variants.map((variant: any) => ({
        companyId,
        spotId: newSpotId,
        fileId: variant.fileId,
        weight: variant.weight,
        mixPoint: variant.mixPoint,
      })),
    });

    // 7. spot slots
    onClose();
    navigate(`/spots/${newSpotId}/overview?auto_suggest=true`);
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Select new spot location"
      source="waveSpot"
      formSubmitterProps={{
        preSubmit: async (values) => {
          await handleSubmit(values);
          onClose();
          return null;
        },
      }}
      maxWidth="sm"
      components={{
        ActionButtons: () => null,
      }}
      PaperProps={{
        sx: {
          pb: 0,
          '& .MuiDialogActions-root': {
            pb: 0,
          },
          '& .MuiDialogContent-root': {
            pb: 1,
          },
        },
      }}
      formProps={{
        defaultValues: {
          // stationId: currentSpot?.campaign?.station?.id,
          // campaignId: currentSpot?.campaign?.id,
          // waveId: currentSpot?.wave?.id,
        },
      }}
    >
      <FormSetter
        render={(setValue) => (
          <>
            {/* <FormGetter
              names={['stationId']}
              onChange={() => {
                setValue('campaignId', null);
                setValue('waveId', null);
              }}
            />
            */}
            <FormGetter
              names={['stationId']}
              onChange={() => {
                setValue('daypartId', null);
              }}
            />

            <AutocompleteInput
              name="stationId"
              label="Station"
              required
              mode="hasura"
              source="station"
              filter={{ companyId: { _eq: user.companyId } }}
              selection="id name"
              itemText="name"
              itemValue="id"
            />

            <FormGetter
              names={['stationId']}
              render={({ stationId }) => (
                <AutocompleteInput
                  name="daypartId"
                  label="Daypart"
                  required
                  mode="hasura"
                  filterSelectedOptions
                  orderBy={{ createdAt: 'DESC' }}
                  source="daypart"
                  disableFetch={!stationId}
                  filter={{
                    companyId: { _eq: user.companyId },
                    stationId: { _eq: stationId },
                  }}
                  selection="id name"
                  itemText="name"
                  itemValue="id"
                  disabled={!stationId}
                />
              )}
            />

            {/* 
            <FormGetter
              names={['stationId']}
              render={({ stationId }) => (
                <AutocompleteInput
                  name="campaignId"
                  label="Campaign"
                  required
                  mode="hasura"
                  source="campaign"
                  orderBy={{ createdAt: 'DESC' }}
                  disableFetch={!stationId}
                  disabled={!stationId}
                  filter={{
                    companyId: { _eq: user.companyId },
                    stationId: { _eq: stationId },
                  }}
                  selection="id name"
                  itemText="name"
                  itemValue="id"
                />
              )}
            />

            <FormGetter
              names={['campaignId']}
              render={({ campaignId }) => (
                <AutocompleteInput
                  name="waveId"
                  label="Wave"
                  required
                  mode="hasura"
                  filterSelectedOptions
                  orderBy={{ createdAt: 'DESC' }}
                  source="wave"
                  disableFetch={!campaignId}
                  filter={{
                    companyId: { _eq: user.companyId },
                    campaignId: { _eq: campaignId },
                  }}
                  selection="id name"
                  itemText="name"
                  itemValue="id"
                  disabled={!campaignId}
                />
              )}
            /> */}

            <FormGetter
              names={['stationId', 'campaignId', 'waveId']}
              render={({ stationId, campaignId, waveId }) => {
                const isComplete = stationId;
                return (
                  <Grid xs={12} sx={{ mt: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <Button onClick={onClose}>Cancel</Button>
                      <SubmitButton
                        grid={false}
                        disabled={!isComplete}
                        variant="contained"
                        // onClick={async () => {
                        //   await handleSubmit({
                        //     stationId,
                        //   });
                        //   onClose();
                        // }}
                      >
                        Confirm
                      </SubmitButton>
                    </Stack>
                  </Grid>
                );
              }}
            />
          </>
        )}
      />
    </FormDialog>
  );
}

export function Spot() {
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [currentSpot, setCurrentSpot] = useState<any>(null);
  const { setSelectedStationId } = useAppState();
  const { user } = useContext(UserContext);
  const { selectedStationId } = useContext(AppStateContext);
  const routeParams = useParams() as Record<string, string>;
  const spotId = parseInt(routeParams.id, 10);

  const tableRef = useRef<DataTableExRef>(null);

  return (
    <FormPageLayout
      source="waveSpot"
      breadcrumbs={[
        (item) => ({
          icon: ClipboardListOutline,
          text: item.campaign?.name,
          href: `/campaigns/${item.campaign?.id}`,
        }),
        (item) => ({
          text: `${item.name ?? ''}`,
          copyOnClick: true,
        }),
      ]}
      hideSaveButton
      breadcrumbsDeps={['name', 'wave', 'campaign']}
      defaultRoute="/waves"
      formFetcherProps={{
        onFetch(item) {
          setSelectedStationId(item?.campaign?.station?.id);

          return item;
        },
        onSelection(selections) {
          return [
            ...selections,
            'wave { id name }',
            'campaign { id name station { id name } }',
          ];
        },
      }}
    >
      <HiddenInput name="wave" formSubmitterValueResolver={null} />
      <HiddenInput name="campaign" formSubmitterValueResolver={null} />
      <Grid container alignItems="flex-start" sx={{ pt: 2 }}>
        <FormGetter
          names={['campaign']}
          render={(x) => (
            <>
              {x.campaign ? (
                <FormNavigateButton
                  label="Campaign"
                  url={`/campaigns/${x.campaign?.id}`}
                  text={x.campaign.name}
                  Icon={ClipboardListOutline}
                  color="#001fbf"
                />
              ) : (
                <Grid xs={12} md={3}>
                  <Skeleton width="100%" sx={{ height: 56 }} />
                </Grid>
              )}

              <Grid xs={0} md={8} />
            </>
          )}
        />

        <FormGetter
          names={['wave']}
          render={(x) => (
            <>
              {x.wave ? (
                <FormNavigateButton
                  label="Wave"
                  url={`/waves/${x.wave?.id}`}
                  text={x.wave.name}
                  Icon={WavesIcon}
                  color="#936000"
                />
              ) : (
                <Grid xs={12} md={4}>
                  <Skeleton width="100%" sx={{ height: 56 }} />
                </Grid>
              )}

              <Grid xs={0} md={8} />
            </>
          )}
        />

        <FormInput
          name="name"
          label="Spot"
          required
          sm={6}
          md={3}
          sx={{
            fieldset: {
              borderColor: 'brown',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <ViewGridOutline htmlColor="brown" />
              </InputAdornment>
            ),
          }}
        />
        <AutocompleteInput
          name="daypartId"
          sm={6}
          label="Daypart"
          required
          mode="hasura"
          source="daypart"
          filter={{
            companyId: { _eq: user.companyId },
            stationId: { _eq: selectedStationId },
          }}
          selection="id name"
          itemText="name"
          itemValue="id"
          md={3}
        />
        <DateInput name="startDate" label="From date" required md={3} sm={6} />
        <DateInput name="endDate" label="To date" required md={3} sm={6} />
        <Grid xs={12} container>
          <SelectInput
            name="type"
            label="Type"
            items={SpotTypes}
            required
            md={3}
            sm={6}
          />

          <ConditionalInput
            deps={['type']}
            variants={[
              {
                if: (y) => y.type !== SpotType.CONNECTED,
                input: (y) => (
                  <FormInput
                    name="numberPerDay"
                    label="Spots/day"
                    type="number"
                    clearable
                    required
                    md={3}
                    sm={6}
                  />
                ),
              },
            ]}
          />

          <ConditionalInput
            deps={['type']}
            variants={[
              {
                if: (y) => y.type === SpotType.CONNECTED,
                input: (y) => (
                  <FormInput
                    name="connectionId"
                    label="Connection ID"
                    required
                    md={3}
                    sm={6}
                  />
                ),
              },
            ]}
          />

          <ConditionalInput
            deps={['type']}
            variants={[
              {
                if: (y) => y.type === SpotType.CONNECTED,
                input: (y) => (
                  <FormInput
                    name="connectionOffset"
                    label="Connection offset"
                    required
                    md={3}
                    sm={6}
                  />
                ),
              },
            ]}
          />
          {/* 
            <ConditionalInput
              deps={['type']}
              variants={[
                {
                  if: (y) => y.type === SpotType.TIME_RESTRICT,
                  input: (y) => (
                    <SelectInput
                      name="weight"
                      label="Weight"
                      required
                      md={3}
                      sm={6}
                      items={SpotWeights}
                    />
                  ),
                },
              ]}
            /> */}
          <Box sx={{ pt: 1.7, pl: 2 }}>
            <ConditionalInput
              deps={['type']}
              variants={[
                {
                  if: (y) => y.type === SpotType.TIME_RESTRICT,
                  input: (y) => (
                    <CheckboxInput
                      grid={false}
                      name="appendToEmptyBlocks"
                      label="Append to empty blocks"
                      // sx={{ mt: 2 }}
                    />
                  ),
                },
              ]}
            />
          </Box>
        </Grid>

        <Grid xs={12} container>
          <Grid md={6}>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormGetter
                names={['type']}
                render={(x) => (
                  <Button
                    disabled={x.type === SpotType.CONNECTED}
                    variant="contained"
                    startIcon={<Calendar />}
                    sx={{ mt: 0.7 }}
                    href="overview"
                    color="primary"
                  >
                    Open Planning
                  </Button>
                )}
              />
              <FormGetter
                names={[
                  'id',
                  'name',
                  'daypartId',
                  'type',
                  'startDate',
                  'endDate',
                  'numberPerDay',
                  'connectionId',
                  'connectionOffset',
                  'appendToEmptyBlocks',
                  'campaign',
                  'wave',
                  'weight',
                ]}
                render={(x) => (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<ContentCopy />}
                    sx={{ mt: 0.7 }}
                    onClick={() => {
                      setCurrentSpot(x);
                      setCopyDialogOpen(true);
                    }}
                  >
                    Duplicate Spot
                  </Button>
                )}
              />
            </Stack>
          </Grid>
          <Grid
            md={6}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <SubmitButton
              grid={false}
              variant="contained"
              activateOnDirty
              sx={{ ml: 'auto' }}
              startIcon={<Save />}
            >
              Save changes
            </SubmitButton>
          </Grid>
        </Grid>

        <Grid container xs={12} sx={{ mt: 2, alignItems: 'flex-start' }}>
          <FormGetter
            names={['id', 'companyId']}
            render={(x) => {
              return (
                <>
                  <Grid xs={12}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="subtitle1">Variants</Typography>
                      <Button
                        startIcon={<Add />}
                        variant="outlined"
                        size="small"
                        sx={{ whiteSpace: 'pre' }}
                        onClick={() => {
                          tableRef.current?.openFormDialog();
                        }}
                      >
                        Add new
                      </Button>
                    </Stack>
                  </Grid>
                  <UploadFile tableRef={tableRef} />
                </>
              );
            }}
          />
        </Grid>
      </Grid>
      <FormGetter
        names={['campaign', 'id']}
        render={(x) => {
          return (
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
              }}
            >
              <SpotVariantsTable
                id="spot-variants-table"
                disableInitialization={!x.id}
                spotId={spotId}
                ref={tableRef}
                companyId={user.companyId}
                pageSizeOptions={[10, 20, 50]}
                sx={{
                  minHeight: 400,
                  mx: -2,
                  borderColor: 'transparent',
                }}
              />
            </Box>
          );
        }}
      />
      <CopySpotDialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        currentSpot={currentSpot}
        user={user}
      />
    </FormPageLayout>
  );
}
